import {
  ExportFiltersInputApprovalStatusItem,
  ExportFiltersInputFinancialStatusItem,
  ExportFiltersInputFulfillmentStatusItem,
  ExportFiltersInputStatusItem,
  OrderLabelInput,
  type ExportFiltersInput,
} from '@nucleus-commerce/api-client/admin';

import type { OrderListParams } from '../api/orders-api';

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

const list = (value: unknown): string[] | undefined => text(value)?.split(',');

const member = <T extends string>(allowed: Record<string, T>, value: string): value is T =>
  (Object.values(allowed) as string[]).includes(value);

const listOf = <T extends string>(allowed: Record<string, T>, value: unknown): T[] | undefined =>
  list(value)?.filter((item): item is T => member(allowed, item));

const oneOf = <T extends string>(allowed: Record<string, T>, value: unknown): T | undefined => {
  const found = text(value);
  return found !== undefined && member(allowed, found) ? found : undefined;
};

/**
 * The orders export takes the list's filters in its own shape (arrays, `from`/`to` for the placed
 * range). Negated filters (`status_not`) have no export equivalent and are left out; the export
 * dialog says the file follows the positive filters only.
 */
export function exportFiltersFromParams(params: OrderListParams): ExportFiltersInput {
  const filters: ExportFiltersInput = {
    from: text(params.placedAt_gte),
    to: text(params.placedAt_lte),
    status: listOf(ExportFiltersInputStatusItem, params.status),
    financialStatus: listOf(ExportFiltersInputFinancialStatusItem, params.financialStatus),
    fulfillmentStatus: listOf(ExportFiltersInputFulfillmentStatusItem, params.fulfillmentStatus),
    approvalStatus: listOf(ExportFiltersInputApprovalStatusItem, params.approvalStatus),
    customerId: text(params.customerId),
    q: text(params.q),
    tags: list(params.tags),
    hasReturns:
      params.hasReturns === 'true' ? true : params.hasReturns === 'false' ? false : undefined,
    workflowStage: text(params.workflowStage),
    label: oneOf(OrderLabelInput, params.label),
  };
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined));
}

/** True when the list carries a negated filter the export cannot repeat. */
export function hasNegatedFilters(params: OrderListParams): boolean {
  return Object.keys(params).some((key) => key.endsWith('_not'));
}
